import * as React from 'react';
import Box from '@mui/material/Box';
import { useParams, useHistory } from 'react-router-dom';
import { getDownloadURL, ref as ref_storage, uploadBytesResumable } from 'firebase/storage';
import { useState, useEffect } from 'react';
import { storage } from '../../Firebase/firebase.config';
import { FiPlus, FiX, FiUpload, FiCheckCircle } from 'react-icons/fi';
import Button from 'react-bootstrap/Button';
import { getDatabase, ref, get, set, onValue, push } from 'firebase/database';

import { styled } from '@mui/material/styles';
import SideNav from '../SideNav';

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

// Shared font stack — inline styling per request, no separate CSS file.
const FONT_STACK = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";

// A single article block's shape:
// { id, file, text, status: 'idle' | 'uploading' | 'done' | 'error', url }
const makeBlock = (id) => ({ id, file: null, text: '', status: 'idle', url: '' });

const Article = () => {
    const { id } = useParams();
    const history = useHistory();
    const database = getDatabase();

    // Dynamic list of article upload blocks — starts with just one.
    const [blocks, setBlocks] = useState([makeBlock(1)]);
    const nextBlockId = React.useRef(2);

    // Existing uploaded articles pulled from Firebase (read-only gallery)
    const [savedArticles, setSavedArticles] = useState([]);
    const [description, setDescription] = useState('');
    const [descriptionTxt, setDescriptionTxt] = useState('');

    // Shown after the final "Complete Profile" submit
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        const slotKeys = [1, 2, 3, 4, 5, 6];
        const unsubscribers = slotKeys.map((slot) =>
            onValue(ref(database, `Article/${id}/${slot}`), (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const url = data[`url${slot}`];
                    const text = data[`text${slot}`] || '';
                    setSavedArticles((prev) => {
                        const withoutThisSlot = prev.filter((item) => item.slot !== slot);
                        return url
                            ? [...withoutThisSlot, { slot, url, text }].sort((a, b) => a.slot - b.slot)
                            : withoutThisSlot;
                    });
                }
            })
        );

        onValue(ref(database, `Article/${id}/description`), (snapshot) => {
            if (snapshot.exists()) {
                setDescriptionTxt(snapshot.val().description || '');
            }
        });

        return () => unsubscribers.forEach((unsub) => unsub && unsub());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const putData = (key, data) => set(ref(database, key), data);

    // ── Block helpers ──
    const updateBlock = (blockId, patch) => {
        setBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, ...patch } : b)));
    };

    const addBlock = () => {
        setBlocks((prev) => [...prev, makeBlock(nextBlockId.current++)]);
    };

    const removeBlock = (blockId) => {
        setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    };

    // Each block uploads independently to the next free numbered slot,
    // same pattern as License.js, so existing articles are never overwritten.
    const uploadBlock = (blockId) => {
        const block = blocks.find((b) => b.id === blockId);
        if (!block || !block.file) {
            alert('Please select an image to upload.');
            return;
        }

        const usedSlots = savedArticles.map((a) => a.slot);
        let targetSlot = 1;
        while (usedSlots.includes(targetSlot)) targetSlot++;

        updateBlock(blockId, { status: 'uploading' });

        const imgRef = ref_storage(storage, `Article/${id}/Article/${targetSlot}`);
        const uploadTask = uploadBytesResumable(imgRef, block.file);

        uploadTask
            .then((snapshot) => getDownloadURL(snapshot.ref))
            .then((url) => {
                putData(`Article/${id}/${targetSlot}`, {
                    [`url${targetSlot}`]: url,
                    [`text${targetSlot}`]: block.text,
                });
                updateBlock(blockId, { status: 'done', url });
            })
            .catch((error) => {
                console.error('Error uploading article:', error);
                updateBlock(blockId, { status: 'error' });
                alert('Upload failed. Please try again.');
            });
    };

    const uploadText = () => {
        putData(`Article/${id}/description`, { description });
    };

    // Final submit — mark the profile as complete instead of routing onward.
    const handleCompleteProfile = () => {
        setCompleted(true);
    };

    // ── Completion screen ──
    if (completed) {
        return (
            <Box sx={{ display: 'flex' }}>
                <SideNav id={id} />
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        boxSizing: 'border-box',
                    }}
                >
                    <DrawerHeader />
                    <div
                        style={{
                            width: '100%',
                            maxWidth: '520px',
                            textAlign: 'center',
                            background: '#fff',
                            padding: '3rem 2rem',
                            borderRadius: '20px',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                            fontFamily: FONT_STACK,
                            boxSizing: 'border-box',
                        }}
                    >
                        <FiCheckCircle size={56} color="#16a34a" />
                        <h2 style={{ marginTop: '1.25rem', color: '#1e293b', fontWeight: 700 }}>
                            Profile Completed!
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', marginTop: '0.5rem' }}>
                            Your profile has been submitted successfully. Our team will review
                            it shortly.
                        </p>
                    </div>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <SideNav id={id} />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, sm: 3 },
                    display: 'flex',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                }}
            >
                <DrawerHeader />

                {/* Inline responsive rules — inline style objects can't express
                    media queries, so this small scoped block handles mobile only. */}
                <style>{`
                    @media (max-width: 600px) {
                        .article-mobile-stack { padding: 0 !important; }
                        .article-mobile-stack h1 { font-size: 1.3rem !important; }
                    }
                `}</style>

                <div
                    className="article-mobile-stack"
                    style={{
                        width: '100%',
                        maxWidth: '720px',
                        margin: '0 auto',
                        fontFamily: FONT_STACK,
                        boxSizing: 'border-box',
                    }}
                >
                    <h1 style={{ color: '#126ca8', fontWeight: 700, fontSize: '1.6rem', marginBottom: '1.5rem' }}>
                        Article
                    </h1>

                    {/* ── Upload blocks ── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {blocks.map((block, index) => (
                            <div
                                key={block.id}
                                style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '14px',
                                    padding: '1.25rem',
                                    background: '#fff',
                                    boxShadow: '0 2px 10px rgba(15,23,42,0.05)',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.75rem',
                                    }}
                                >
                                    <h5 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
                                        {index === 0 ? 'Upload Article' : `Additional Article ${index}`}
                                    </h5>
                                    {blocks.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeBlock(block.id)}
                                            aria-label="Remove this article block"
                                            style={{
                                                border: 'none',
                                                background: '#fee2e2',
                                                color: '#dc2626',
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <FiX />
                                        </button>
                                    )}
                                </div>

                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        border: '1.5px dashed #cbd5e1',
                                        borderRadius: '10px',
                                        padding: '0.65rem 0.9rem',
                                        cursor: 'pointer',
                                        fontSize: '0.88rem',
                                        color: '#64748b',
                                        background: '#f8fafc',
                                        marginBottom: '0.75rem',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => updateBlock(block.id, { file: e.target.files[0] })}
                                        style={{ display: 'none' }}
                                    />
                                    <span
                                        style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {block.file ? block.file.name : 'Choose file…'}
                                    </span>
                                </label>

                                <input
                                    type="text"
                                    placeholder="Enter details"
                                    value={block.text}
                                    onChange={(e) => updateBlock(block.id, { text: e.target.value })}
                                    style={{
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        padding: '0.6rem 0.9rem',
                                        fontSize: '0.9rem',
                                        fontFamily: FONT_STACK,
                                        marginBottom: '0.9rem',
                                        outline: 'none',
                                    }}
                                />

                                <Button
                                    onClick={() => uploadBlock(block.id)}
                                    disabled={block.status === 'uploading'}
                                    variant="primary"
                                    type="button"
                                    style={{
                                        width: '100%',
                                        backgroundColor: '#0073cf',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '0.6rem 0',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.4rem',
                                        fontFamily: FONT_STACK,
                                    }}
                                >
                                    {block.status === 'uploading' ? (
                                        'Uploading…'
                                    ) : block.status === 'done' ? (
                                        'Uploaded ✓'
                                    ) : (
                                        <>
                                            Upload <FiUpload />
                                        </>
                                    )}
                                </Button>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={addBlock}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem',
                            width: '100%',
                            marginTop: '1rem',
                            padding: '0.7rem 0',
                            border: '1.5px dashed #0073cf',
                            borderRadius: '10px',
                            background: 'transparent',
                            color: '#0073cf',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            fontFamily: FONT_STACK,
                        }}
                    >
                        <FiPlus /> Add More
                    </button>

                    {/* ── Write-your-own-article section ── */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            margin: '2rem 0 1.25rem',
                        }}
                    >
                        <h5 style={{ color: '#d32f2f', fontWeight: 600, fontSize: '0.95rem', userSelect: 'none' }}>
                            Or You Can Write Your Article Below
                        </h5>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: '1px solid #e2e8f0',
                            padding: '1.25rem',
                            borderRadius: '14px',
                            background: '#fff',
                            boxShadow: '0 2px 10px rgba(15,23,42,0.05)',
                            boxSizing: 'border-box',
                        }}
                    >
                        <h5 style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.75rem' }}>
                            Write your description:
                        </h5>
                        <textarea
                            rows={4}
                            placeholder="Please write here..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{
                                whiteSpace: 'pre-line',
                                width: '100%',
                                boxSizing: 'border-box',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                padding: '0.7rem 0.9rem',
                                fontSize: '0.9rem',
                                fontFamily: FONT_STACK,
                                resize: 'vertical',
                                marginBottom: '0.9rem',
                            }}
                        />

                        <Button
                            onClick={uploadText}
                            variant="primary"
                            type="button"
                            style={{
                                width: '100%',
                                backgroundColor: '#0073cf',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '0.6rem 0',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                fontFamily: FONT_STACK,
                            }}
                        >
                            Save Description
                        </Button>

                        {descriptionTxt && (
                            <>
                                <h6 style={{ fontWeight: 600, color: '#1e293b', marginTop: '1.25rem', marginBottom: '0.4rem' }}>
                                    Description
                                </h6>
                                <p style={{ whiteSpace: 'pre-line', color: '#475569', fontSize: '0.9rem', margin: 0 }}>
                                    {descriptionTxt}
                                </p>
                            </>
                        )}
                    </div>

                    {/* ── Saved articles gallery ── */}
                    <h4 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 600, color: '#1e293b' }}>
                        All Documents
                    </h4>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                        {savedArticles.length === 0 ? (
                            <p style={{ color: '#94a3b8', fontSize: '0.88rem' }}>No articles uploaded yet.</p>
                        ) : (
                            savedArticles.map(({ slot, url, text }) => (
                                <div
                                    key={slot}
                                    style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        padding: '0.75rem',
                                        background: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        maxWidth: '320px',
                                    }}
                                >
                                    <img
                                        src={url}
                                        alt={`Article ${slot}`}
                                        onClick={() => window.open(url, '_blank')}
                                        style={{
                                            width: 80,
                                            height: 80,
                                            objectFit: 'cover',
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                        }}
                                    />
                                    {text && (
                                        <p style={{ fontSize: '0.85rem', color: '#475569', margin: 0 }}>{text}</p>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* ── Final submit ── */}
                    <div style={{ textAlign: 'center', marginTop: '2.5rem', marginBottom: '2rem' }}>
                        <Button
                            variant="primary"
                            onClick={handleCompleteProfile}
                            style={{
                                padding: '0.7rem 2rem',
                                borderRadius: '10px',
                                fontWeight: 600,
                                fontFamily: FONT_STACK,
                            }}
                        >
                            Complete Profile
                        </Button>
                    </div>
                </div>
            </Box>
        </Box>
    );
};

export default Article;